import React, { useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  AsyncSelect,
  Button,
  CheckForm,
  Divider,
  Form,
  Grid,
  Group,
  InlineAlert,
  Input,
  Page,
  PositiveNumberInput,
  Section,
  Select,
  TextArea,
  Tooltip,
  Typography,
} from "@samskip/frontend-components";
import { PageProps, navigate } from "gatsby";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import {
  TransferPlace,
  ShipmentType,
  AddressType,
  PROCESSING,
  MAX_WEIGHT,
  MAX_WIDTH,
  MAX_LENGTH,
  MAX_HEIGHT,
  CommonConstants,
  LabelConstants,
  NavigationConstants,
} from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks";
import {
  Commodity,
  ContainerType,
  PackageType,
  RegistryTypes,
  PortTypes,
  SelectOption,
  UserProfileViewModel,
  QuoteAddress,
  QuoteVM,
  CargoPackageParentType,
} from "@assets/types";
import {
  ArrayUtilities,
  IncotermUtilities,
  LabelMapperUtilities,
  StringUtilities,
} from "@assets/utilities";
import { getIncotermPorts } from "@assets/utilities/incotermUtilities";
import {
  commodityLabelMapper,
  commodityValueMapper,
  incotermLabelMapper,
  incotermValueMapper,
  packageTypeLabelMapper,
  packageTypeValueMapper,
  portLabelMapper,
  portValueMapper,
} from "@assets/utilities/labelMapperUtils";
import {
  getTotalVolume,
  getTotalWeight,
  isPackageRefrigerated,
} from "@assets/utilities/packageUtilities";
import { getErrorMessage } from "@assets/utilities/translateUtilities";
import { WithFormController, InputGroup, LoadingModal } from "@components";
import PortService from "@services/PortService";
import QuoteService from "@services/QuoteService";
import {
  commoditiesState,
  getAllCommodities,
  getMachineryCommodities,
  machineryCommoditiesState,
} from "@state/slices/commoditySlice";
import { getPorts, portsState } from "@state/slices/portsSlice";
import {
  containerTypesState,
  getContainerTypes,
  getIncoterms,
  getPackageTypes,
  incotermsState,
  packageTypesState,
} from "@state/slices/registrySlice";
import { userProfileState } from "@state/slices/userProfileSlice";

interface LocationState {
  quote: QuoteVM;
}

type QuoteCreateProps = PageProps<null, null, LocationState>;

interface QuoteForm {
  ContactName: string;
  ContactEmail: string;
  AddressType: AddressType;
  ShipmentQuantity: string;
  ShipmentType: ShipmentType;
  ShipmentWeight: string;
  ShipmentLength: string;
  ShipmentWidth: string;
  ShipmentHeight: string;
  ShipmentPackaging: CargoPackageParentType | null;
  Commodity: Commodity | null;
  IsTemperatureControlled: boolean;
  ShipmentTemperature: SelectOption<number> | null;
  IsStackable: boolean;
  DangerousGoods: boolean;
  Comment: string;
  PickupPlace: TransferPlace;
  PickupPostalCode: string;
  PLR: PortTypes.Port | null; // place of receipt
  DropOffPlace: TransferPlace;
  DropOffPostalCode: string;
  PFD: PortTypes.Port | null; // place of delivery
  POL: PortTypes.Port | null;
  POD: PortTypes.Port | null;
  Incoterms: RegistryTypes.Incoterm | null;
  IncotermPoint: PortTypes.Port | null;
}

const defaultFormValues: QuoteForm = {
  ContactName: "",
  ContactEmail: "",
  AddressType: AddressType.CEE,
  ShipmentQuantity: "",
  ShipmentType: ShipmentType.FCL,
  ShipmentWeight: "",
  ShipmentLength: "",
  ShipmentWidth: "",
  ShipmentHeight: "",
  ShipmentPackaging: null,
  Commodity: null,
  IsTemperatureControlled: false,
  ShipmentTemperature: null,
  IsStackable: false,
  DangerousGoods: false,
  Comment: "",
  PickupPlace: TransferPlace.ADDRESS,
  PickupPostalCode: "",
  PLR: null,
  DropOffPlace: TransferPlace.ADDRESS,
  DropOffPostalCode: "",
  PFD: null,
  POL: null,
  POD: null,
  Incoterms: null,
  IncotermPoint: null,
};

const searchPort = (searchString: string): Promise<PortTypes.Port[]> => {
  return PortService.searchPorts(searchString);
};

const getFirstPort = async (searchedAddress: string) => {
  const ports = await searchPort(searchedAddress);
  return ports.length > 0 ? ports[0] : null;
};

const formatNumber = (num: number | null): string => {
  if (num !== null && !isNaN(num)) {
    return StringUtilities.numberCommaDecimal(num);
  }
  return "N/A";
};

const editDefaultMapper = async (
  quote: QuoteVM,
  packageTypes: PackageType[],
  containerTypes: ContainerType[],
  ports: PortTypes.Port[],
  incoterms: RegistryTypes.Incoterm[],
  userProfile: UserProfileViewModel | undefined
): Promise<QuoteForm> => {
  const containerObject = ArrayUtilities.getObject(
    containerTypes,
    quote.PackageCode,
    "PackageCode"
  );

  const packageObject = ArrayUtilities.getObject(
    packageTypes,
    quote.PackageCode,
    "PackageCode"
  );
  return {
    ContactName: userProfile?.User?.Username || defaultFormValues.ContactName,
    ContactEmail: userProfile?.Access?.Email || defaultFormValues.ContactEmail,
    AddressType: quote.AddressType,
    ShipmentQuantity: quote.Quantity.toString(),
    ShipmentType: quote.ShipmentType,
    ShipmentWeight: quote.PackageSize.Weight.toString(),
    ShipmentLength:
      quote.PackageSize.Length?.toString() || defaultFormValues.ShipmentLength,
    ShipmentWidth:
      quote.PackageSize.Width?.toString() || defaultFormValues.ShipmentWidth,
    ShipmentHeight:
      quote.PackageSize.Height?.toString() || defaultFormValues.ShipmentHeight,
    ShipmentPackaging:
      (quote.ShipmentType === ShipmentType.FCL
        ? containerObject
        : packageObject) || defaultFormValues.ShipmentPackaging,
    Commodity: quote.Commodity,
    IsTemperatureControlled:
      Number.isFinite(quote.Temperature) ||
      defaultFormValues.IsTemperatureControlled,
    ShipmentTemperature: Number.isFinite(quote.Temperature)
      ? LabelMapperUtilities.singleValueToSelectOptionMapper(quote.Temperature)
      : defaultFormValues.ShipmentTemperature,
    IsStackable: quote.Stackable,
    DangerousGoods: quote.DangerousGoods,
    Comment: quote.OtherInfo,
    PickupPlace: quote.PointFrom ? TransferPlace.ADDRESS : TransferPlace.PORT,
    PickupPostalCode:
      quote.PointFrom && quote.FromAddress
        ? quote.FromAddress.PostCode
        : defaultFormValues.PickupPostalCode,
    PLR:
      quote.PointFrom && quote.FromAddress && quote.FromAddress.City
        ? await getFirstPort(quote.FromAddress.City)
        : defaultFormValues.PLR,
    DropOffPlace: quote.PointTo ? TransferPlace.ADDRESS : TransferPlace.PORT,
    DropOffPostalCode:
      quote.PointTo && quote.ToAddress
        ? quote.ToAddress.PostCode
        : defaultFormValues.DropOffPostalCode,
    PFD:
      quote.PointTo && quote.ToAddress && quote.ToAddress.City
        ? await getFirstPort(quote.ToAddress.City)
        : defaultFormValues.PFD,
    POL:
      ArrayUtilities.getObject(ports, quote.LoadPort, "PointCode") ||
      defaultFormValues.POL,
    POD:
      ArrayUtilities.getObject(ports, quote.DischargePort, "PointCode") ||
      defaultFormValues.POD,
    Incoterms:
      ArrayUtilities.getObject(incoterms, quote.Incoterm, "TermsCode") ||
      defaultFormValues.Incoterms,
    IncotermPoint: quote.IncotermPoint
      ? await getFirstPort(quote.IncotermPoint)
      : defaultFormValues.IncotermPoint,
  };
};

const QuoteCreate: React.FC<QuoteCreateProps> = ({
  location,
}: QuoteCreateProps) => {
  const dropdownSchema = {
    label: yup.string(),
    value: yup.mixed(),
  };

  const { t } = useTranslation();
  const userProfile = useAppSelector(userProfileState).userProfile;

  const [commodityOptions, setCommodityOptions] = useState<Commodity[]>([]);
  const [uniPackageType, setUniPackageType] =
    useState<CargoPackageParentType | null>(null);
  const [defaultPlaceCities, setDefaultPlaceCities] = useState<
    PortTypes.Port[]
  >([]);
  const [temperatureOptions, setTemperatureOptions] = useState<
    SelectOption<number>[]
  >([]);
  const [defaultTemperatureOptions, setDefaultTemperatureOptions] = useState<
    SelectOption<number>[]
  >([]);
  const [fallbackPort, setFallbackPort] = useState<PortTypes.Port | null>(null);
  const [filteredIncoterms, setFilteredIncoterms] = useState<
    RegistryTypes.Incoterm[]
  >([]);
  const [incotermPlaceOptions, setIncotermPlaceOptions] = useState<
    PortTypes.Port[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const ports = useAppSelector(portsState);
  const containerTypes = useAppSelector(containerTypesState);
  const packageTypes = useAppSelector(packageTypesState);
  const incoterms = useAppSelector(incotermsState);
  const commodities = useAppSelector(commoditiesState);
  const machineryCommodities = useAppSelector(machineryCommoditiesState);
  const dispatch = useAppDispatch();

  const schema = yup.object({
    ContactName: yup
      .string()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    ContactEmail: yup
      .string()
      .email()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    PickupPostalCode: yup.string(),
    PLR: yup
      .mixed()
      .nullable()
      .when("PickupPlace", {
        is: TransferPlace.ADDRESS,
        then: () =>
          yup
            .object()
            .notOneOf([yup.ref("PFD")], "ERROR_PFD_NOT_SAME_PLR")
            .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
            .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
      }),
    DropOffPostalCode: yup.string(),
    PFD: yup
      .mixed()
      .nullable()
      .when("DropOffPlace", {
        is: TransferPlace.ADDRESS,
        then: () =>
          yup
            .object()
            .notOneOf([yup.ref("PLR")], "ERROR_PLR_NOT_SAME_PFD")
            .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
            .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
      }),
    POL: yup
      .mixed()
      .nullable()
      .when("PickupPlace", {
        is: TransferPlace.PORT,
        then: () =>
          yup
            .object()
            .notOneOf([yup.ref("POD")], "ERROR_POL_NOT_SAME_POD")
            .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
            .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
      }),
    POD: yup
      .mixed()
      .nullable()
      .when("DropOffPlace", {
        is: TransferPlace.PORT,
        then: () =>
          yup
            .object()
            .notOneOf([yup.ref("POL")], "ERROR_POD_NOT_SAME_POL")
            .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
            .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
      }),
    Incoterms: yup
      .object()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
      .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    IncotermPoint: yup.object(),
    ShipmentQuantity: yup
      .string()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    ShipmentPackaging: yup
      .object()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
      .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    ShipmentWeight: yup
      .string()
      .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
      .test(
        "maxWeight",
        LabelConstants.ERROR_LABELS.MAX_N_WEIGHT_EXCEEDED,
        (weight): boolean => {
          return weight != undefined && parseInt(weight) <= MAX_WEIGHT;
        }
      ),
    ShipmentLength: yup.mixed().when("ShipmentType", {
      is: (val: ShipmentType) =>
        val === ShipmentType.LCL || val === ShipmentType.CAR,
      then: () =>
        yup
          .string()
          .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
          .test(
            "maxLength",
            LabelConstants.ERROR_LABELS.MAX_N_EXCEEDED,
            (length): boolean => {
              return length != undefined && parseInt(length) <= MAX_LENGTH;
            }
          ),
    }),
    ShipmentWidth: yup.mixed().when("ShipmentType", {
      is: (val: ShipmentType) =>
        val === ShipmentType.LCL || val === ShipmentType.CAR,
      then: () =>
        yup
          .string()
          .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
          .test(
            "maxWidth",
            LabelConstants.ERROR_LABELS.MAX_N_EXCEEDED,
            (width): boolean => {
              return width != undefined && parseInt(width) <= MAX_WIDTH;
            }
          ),
    }),
    ShipmentHeight: yup.mixed().when("ShipmentType", {
      is: (val: ShipmentType) =>
        val === ShipmentType.LCL || val === ShipmentType.CAR,
      then: () =>
        yup
          .string()
          .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
          .test(
            "maxHeight",
            LabelConstants.ERROR_LABELS.MAX_N_EXCEEDED,
            (height): boolean => {
              return height != undefined && parseInt(height) <= MAX_HEIGHT;
            }
          ),
    }),
    Commodity: yup
      .object()
      .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    ShipmentTemperature: yup
      .mixed()
      .nullable()
      .when("IsTemperatureControlled", {
        is: true,
        then: () =>
          yup
            .object()
            .shape(dropdownSchema)
            .required(LabelConstants.ERROR_LABELS.REQUIRED_FIELD)
            .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
      }),
  });
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    setFocus,
  } = useForm({
    defaultValues: defaultFormValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  /**
   * Update the selectable incoterm places based on what incoterm was selected
   * and what inputs were filled in
   */
  const updateIncotermPlaces = () => {
    const selectedIncoterm = getValues("Incoterms");

    const incotermPlaces = getIncotermPorts(selectedIncoterm, {
      PLR: getValues("PLR"),
      POL: getValues("POL"),
      PFD: getValues("PFD"),
      POD: getValues("POD"),
    });
    setIncotermPlaceOptions(incotermPlaces);
    setValue("IncotermPoint", incotermPlaces[0]);
  };

  const setQuoteValues = () => {
    if (location?.state && location?.state?.quote) {
      editDefaultMapper(
        location.state.quote,
        packageTypes,
        containerTypes,
        ports,
        incoterms,
        userProfile
      ).then((quoteDefaultValues) => {
        reset(quoteDefaultValues);
        updateIncotermPlaces();
      });
    }
  };

  useEffect(() => {
    const getIcelandPorts = async () => {
      return PortService.searchPorts(null, "IS");
    };

    setQuoteValues();
    dispatch(getPorts());
    dispatch(getContainerTypes());
    dispatch(getPackageTypes());
    dispatch(getIncoterms());

    dispatch(getAllCommodities());
    dispatch(getMachineryCommodities());

    getIcelandPorts().then((icelandPorts) =>
      setDefaultPlaceCities(icelandPorts)
    );

    const defaultTemperatureOptions =
      LabelMapperUtilities.listToSelectOptionMapper(
        ArrayUtilities.range(
          CommonConstants.CONTAINER_MIN_TEMP,
          CommonConstants.CONTAINER_MAX_TEMP
        )
      );
    setDefaultTemperatureOptions(defaultTemperatureOptions);
    setTemperatureOptions(defaultTemperatureOptions);
  }, []);

  useEffect(() => {
    setQuoteValues();
  }, [ports, packageTypes, containerTypes, incoterms]);

  useEffect(() => {
    setValue(
      "ContactName",
      userProfile?.User.Username || defaultFormValues.ContactName
    );
    setValue(
      "ContactEmail",
      userProfile?.Access.Email || defaultFormValues.ContactEmail
    );
  }, [userProfile]);

  useEffect(() => {
    const fallback_POD_POL_Port = ArrayUtilities.getObject<PortTypes.Port>(
      ports,
      "NLRTM",
      "PointCode"
    );
    setFallbackPort(fallback_POD_POL_Port || null);
  }, [ports]);

  useEffect(() => {
    const carPackaging = ArrayUtilities.getObject<PackageType>(
      packageTypes,
      "UNI",
      "PackageCode"
    );
    setUniPackageType(carPackaging || null);
  }, [packageTypes]);

  const handleSetAllCommoditiesDropdown = () => {
    setCommodityOptions(commodities);
  };

  /**
   * Filter out the incoterms that depend only on PLR/PFD
   * but they are not filled out in the form
   * and reset the value if it was previously selected
   * @returns Filtered array of incoterms that can be selected
   */
  const filterIncoterms = () => {
    const { filteredIncoterms: filteredTerms, wasSelectedRemoved } =
      IncotermUtilities.getFilteredIncoterms(
        incoterms,
        {
          PLR: getValues("PLR"),
          POL: getValues("POL"),
          PFD: getValues("PFD"),
          POD: getValues("POD"),
        },
        getValues("Incoterms")?.TermsCode || null
      );
    if (wasSelectedRemoved) {
      setValue("Incoterms", defaultFormValues.Incoterms);
      updateIncotermPlaces();
    }
    setFilteredIncoterms(filteredTerms);
  };

  useEffect(() => {
    filterIncoterms();
  }, [incoterms]);

  useEffect(() => {
    handleSetAllCommoditiesDropdown();
  }, [commodities]);

  // Workaround to focus in the order the error appear in the errors.
  // Keep in mind that react-hook-form focuses first on native input and only after on components with from Controller
  // So this can't always focus on the topmost element
  // See: https://github.com/react-hook-form/react-hook-form/issues/3232
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0] as keyof typeof errors;
      setFocus(firstError as keyof QuoteForm);
    }
  }, [errors, setFocus]);

  const setMachineryCommoditiesDropdown = () => {
    setCommodityOptions(machineryCommodities);
  };

  const resetOnPickupPlaceChange = () => {
    setValue("PLR", defaultFormValues.PLR);
    setValue("POL", defaultFormValues.POL);
    setValue("Incoterms", defaultFormValues.Incoterms);
    filterIncoterms();
    updateIncotermPlaces();
  };

  const resetOnDropOffPlaceChange = () => {
    setValue("PFD", defaultFormValues.PFD);
    setValue("POD", defaultFormValues.POD);
    setValue("Incoterms", defaultFormValues.Incoterms);
    filterIncoterms();
    updateIncotermPlaces();
  };

  const resetShipmentTemperature = () => {
    setValue(
      "IsTemperatureControlled",
      defaultFormValues.IsTemperatureControlled
    );
    setValue("ShipmentTemperature", defaultFormValues.ShipmentTemperature);
  };

  const onShipmentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === ShipmentType.CAR) {
      setValue("ShipmentQuantity", "1");
      setValue("ShipmentPackaging", uniPackageType);
      setValue("Commodity", defaultFormValues.Commodity);
      setMachineryCommoditiesDropdown();
      setValue("IsStackable", defaultFormValues.IsStackable);
      setValue("DangerousGoods", true);
    } else {
      setValue("ShipmentQuantity", defaultFormValues.ShipmentQuantity);
      setValue("ShipmentPackaging", defaultFormValues.ShipmentPackaging);
      handleSetAllCommoditiesDropdown();
      setValue("DangerousGoods", defaultFormValues.DangerousGoods);
    }
    setValue("ShipmentLength", defaultFormValues.ShipmentLength);
    setValue("ShipmentWidth", defaultFormValues.ShipmentWidth);
    setValue("ShipmentHeight", defaultFormValues.ShipmentHeight);
    setValue("ShipmentWeight", defaultFormValues.ShipmentWeight);
    resetShipmentTemperature();

    // Set POL and POD for PLR and PFD, based on shipment type and PLR, PFD
    const plr = getValues("PLR"),
      pfd = getValues("PFD");
    if (plr) {
      setPortForCity(plr, "POL", ShipmentType[event.target.value]);
    }

    if (pfd) {
      setPortForCity(pfd, "POD", ShipmentType[event.target.value]);
    }
  };

  const onPackageChange = (packageType: ContainerType) => {
    if (packageType) {
      if (!isPackageRefrigerated(packageType)) {
        resetShipmentTemperature();
      } else {
        setValue("IsTemperatureControlled", true);
      }
    }
  };

  const setShipmentTemperatures = (commodity: Commodity) => {
    if (
      commodity &&
      commodity.LowerTemperature !== null &&
      commodity.HigherTemperature !== null
    ) {
      const temperatureOptions = LabelMapperUtilities.listToSelectOptionMapper(
        ArrayUtilities.range(
          commodity.LowerTemperature,
          commodity.HigherTemperature
        )
      );
      // Reset values for temperature dropdown with commodity temperatures
      setValue("ShipmentTemperature", defaultFormValues.ShipmentTemperature);
      setTemperatureOptions(temperatureOptions);
    } else {
      // Reset values for temperature dropdown with default temperatures
      setValue("ShipmentTemperature", defaultFormValues.ShipmentTemperature);
      setTemperatureOptions(defaultTemperatureOptions);
    }
  };

  const onSubmit = async (data: QuoteForm) => {
    setIsSubmitting(true);

    const fromAddress: QuoteAddress | null =
      getValues("PickupPlace") === TransferPlace.ADDRESS
        ? {
            Address: "",
            City: data.PLR?.FullName || "",
            CountryCode: data.PLR?.CountryCode || "",
            PostCode: data.PickupPostalCode,
          }
        : {
            Address: userProfile?.SelectedCompany.Address1 || "",
            City: data.POL?.FullName || "",
            CountryCode: data.POL?.CountryCode || "",
            PostCode: userProfile?.SelectedCompany.PostCode || "",
          };

    const toAddress: QuoteAddress | null =
      getValues("DropOffPlace") === TransferPlace.ADDRESS
        ? {
            Address: "",
            City: data.PFD?.FullName || "",
            CountryCode: data.PFD?.CountryCode || "",
            PostCode: data.DropOffPostalCode,
          }
        : {
            Address: userProfile?.SelectedCompany.Address1 || "",
            City: data.POD?.FullName || "",
            CountryCode: data.POD?.CountryCode || "",
            PostCode: userProfile?.SelectedCompany.PostCode || "",
          };

    const requestObject: QuoteVM = {
      ContactName: data.ContactName,
      ContactEmail: data.ContactEmail,
      CompanyDetails: userProfile?.SelectedCompany,
      AddressType: data.AddressType,
      ShipmentType: data.ShipmentType,
      Quantity: parseInt(data.ShipmentQuantity ?? "0"),
      PackageCode: data.ShipmentPackaging?.PackageCode || "",
      PackageSize: {
        Weight: parseInt(data.ShipmentWeight ?? "0"),
        Length: parseInt(data.ShipmentLength ?? "0"),
        Width: parseInt(data.ShipmentWidth ?? "0"),
        Height: parseInt(data.ShipmentHeight ?? "0"),
      },
      Commodity: data.Commodity as Commodity,
      Temperature: data.ShipmentTemperature?.value || null,
      DangerousGoods: data.DangerousGoods,
      FromAddress: fromAddress,
      ToAddress: toAddress,
      LoadPort: data.POL?.PointCode || "",
      DischargePort: data.POD?.PointCode || "",
      Incoterm: data.Incoterms?.TermsCode || "",
      IncotermPoint: data.IncotermPoint?.PointCode || "",
      OtherInfo: data.Comment,
      PointFrom: data.PLR?.PointCode || null,
      PointTo: data.PFD?.PointCode || null,
      Stackable: data.IsStackable,
    };

    await QuoteService.createQuote(requestObject)
      .then((res) => {
        navigate(
          `${NavigationConstants.NAVIGATION_LINKS.quotes}/requested/${res}`
        );
      })
      .catch(() => {
        navigate(
          `${NavigationConstants.NAVIGATION_LINKS.quotes}/requested/${PROCESSING}`
        );
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const setPortForCity = (
    selectedCity: PortTypes.Port,
    name: "POD" | "POL",
    shipmentType?: ShipmentType
  ) => {
    PortService.getPortForPool(
      selectedCity.PointCode,
      name,
      shipmentType ?? getValues("ShipmentType")
    ).then((port: PortTypes.Port) => {
      if (port) {
        setValue(name, port);
      } else {
        setValue(name, fallbackPort);
      }
    });
    updateIncotermPlaces();
  };

  const onPointCodeSelect = async (
    port: PortTypes.Port,
    type: "PLR" | "PFD"
  ) => {
    setPortForCity(
      port,
      type === "PLR"
        ? CommonConstants.INCOTERM_POINT.POL
        : CommonConstants.INCOTERM_POINT.POD
    );
    filterIncoterms();
  };

  const getShipmentInput = (shipmentType: ShipmentType) => {
    switch (shipmentType) {
      case ShipmentType.FCL:
        return (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-8">
            <PositiveNumberInput
              errorMessage={getErrorMessage("ShipmentQuantity", errors)}
              fullWidth
              hasError={errors.ShipmentQuantity != null}
              id="shipment-quantity"
              label={`${t("TEXT_QUANTITY")}`}
              placeholder={t("PLACEHOLDER_QUANTITY")}
              required
              {...register("ShipmentQuantity")}
            />

            <WithFormController control={control} name="ShipmentPackaging">
              <Select
                className="md:col-span-2"
                errorMessage={getErrorMessage("ShipmentPackaging", errors)}
                fullWidth
                getOptionLabel={packageTypeLabelMapper}
                getOptionValue={packageTypeValueMapper}
                hasError={errors.ShipmentPackaging != null}
                label={t("LABEL_CONTAINER_TYPE")}
                onChange={onPackageChange}
                options={containerTypes}
                placeholder={t("PLACEHOLDER_SELECT_CONTAINER_TYPE")}
                required
              />
            </WithFormController>
            <PositiveNumberInput
              errorMessage={getErrorMessage("ShipmentWeight", errors, {
                weight: StringUtilities.numberCommaDecimal(MAX_WEIGHT),
              })}
              fullWidth
              hasError={errors.ShipmentWeight != null}
              id="shipment-weight"
              label={`${t("LABEL_UNIT_WEIGHT")} (KG)`}
              placeholder={t("PLACEHOLDER_WEIGHT")}
              required
              {...register("ShipmentWeight")}
            />
            <div className="flex flex-col gap-4 uppercase">
              <Typography textCase="upper">
                {`${t("LABEL_TOTAL_WEIGHT")} (KG) `}
              </Typography>
              {formatNumber(
                getTotalWeight(
                  parseInt(watch("ShipmentQuantity")),
                  parseInt(watch("ShipmentWeight"))
                )
              )}
            </div>
          </div>
        );
      case ShipmentType.LCL:
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
              <PositiveNumberInput
                errorMessage={getErrorMessage("ShipmentQuantity", errors)}
                fullWidth
                hasError={errors.ShipmentQuantity != null}
                id="shipment-quantity"
                label={t("TEXT_QUANTITY")}
                placeholder={t("PLACEHOLDER_QUANTITY")}
                required
                {...register("ShipmentQuantity")}
              />
              <WithFormController control={control} name="ShipmentPackaging">
                <Select
                  errorMessage={getErrorMessage("ShipmentPackaging", errors)}
                  fullWidth
                  getOptionLabel={packageTypeLabelMapper}
                  getOptionValue={packageTypeValueMapper}
                  hasError={errors.ShipmentPackaging != null}
                  label={t("LABEL_UNIT_TYPE")}
                  options={packageTypes}
                  placeholder={t("PLACEHOLDER_SELECT_UNIT_TYPE")}
                  required
                />
              </WithFormController>
              <InputGroup className="col-span-3" gap={8} hideDividerOnMobile>
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentLength", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_LENGTH),
                  })}
                  fullWidth
                  hasError={errors.ShipmentLength != null}
                  id="shipment-length"
                  label={`${t("LABEL_UNIT_LENGTH")} (CM)`}
                  placeholder={t("PLACEHOLDER_LENGTH")}
                  required
                  {...register("ShipmentLength")}
                />
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentWidth", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_WIDTH),
                  })}
                  fullWidth
                  hasError={errors.ShipmentWidth != null}
                  id="shipment-width"
                  label={`${t("LABEL_UNIT_WIDTH")} (CM)`}
                  placeholder={t("PLACEHOLDER_WIDTH")}
                  required
                  {...register("ShipmentWidth")}
                />
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentHeight", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_HEIGHT),
                  })}
                  fullWidth
                  hasError={errors.ShipmentHeight != null}
                  id="shipment-height"
                  label={`${t("LABEL_UNIT_HEIGHT")} (CM)`}
                  placeholder={t("PLACEHOLDER_HEIGHT")}
                  required
                  {...register("ShipmentHeight")}
                />
              </InputGroup>
              <PositiveNumberInput
                errorMessage={getErrorMessage("ShipmentWeight", errors, {
                  weight: StringUtilities.numberCommaDecimal(MAX_WEIGHT),
                })}
                fullWidth
                hasError={errors.ShipmentWeight != null}
                id="shipment-weight"
                label={`${t("LABEL_UNIT_WEIGHT")} (KG)`}
                placeholder={t("PLACEHOLDER_WEIGHT")}
                required
                {...register("ShipmentWeight")}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
              <div className="flex flex-col gap-4 md:col-start-5 uppercase">
                <Typography textCase="upper">
                  {`${t("LABEL_TOTAL_VOLUME")} (M3) `}
                </Typography>
                {formatNumber(
                  getTotalVolume(
                    parseInt(watch("ShipmentQuantity")),
                    parseInt(watch("ShipmentLength")),
                    parseInt(watch("ShipmentWidth")),
                    parseInt(watch("ShipmentHeight"))
                  )
                )}
              </div>
              <div className="flex flex-col gap-4 uppercase">
                <Typography textCase="upper">
                  {`${t("LABEL_TOTAL_WEIGHT")} (KG) `}
                </Typography>
                {formatNumber(
                  getTotalWeight(
                    parseInt(watch("ShipmentQuantity")),
                    parseInt(watch("ShipmentWeight"))
                  )
                )}
              </div>
            </div>
          </>
        );
      case ShipmentType.CAR:
        return (
          <>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
              <PositiveNumberInput
                disabled
                fullWidth
                id="quantity"
                label={`${t("TEXT_QUANTITY")}`}
                placeholder={t("PLACEHOLDER_QUANTITY")}
                {...register("ShipmentQuantity")}
              />
              <WithFormController control={control} name="ShipmentPackaging">
                <Select
                  disabled
                  fullWidth
                  getOptionLabel={packageTypeLabelMapper}
                  getOptionValue={packageTypeValueMapper}
                  label={t("LABEL_UNIT_TYPE")}
                  options={packageTypes}
                  placeholder={t("PLACEHOLDER_SELECT")}
                />
              </WithFormController>
              <InputGroup className="col-span-3" gap={8} hideDividerOnMobile>
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentLength", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_LENGTH),
                  })}
                  fullWidth
                  hasError={errors.ShipmentLength != null}
                  id="shipment-length"
                  label={`${t("LABEL_UNIT_LENGTH")} (CM)`}
                  placeholder={t("PLACEHOLDER_LENGTH")}
                  required
                  {...register("ShipmentLength")}
                />
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentWidth", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_WIDTH),
                  })}
                  fullWidth
                  hasError={errors.ShipmentWidth != null}
                  id="shipment-width"
                  label={`${t("LABEL_UNIT_WIDTH")} (CM)`}
                  placeholder={t("PLACEHOLDER_WIDTH")}
                  required
                  {...register("ShipmentWidth")}
                />
                <PositiveNumberInput
                  errorMessage={getErrorMessage("ShipmentHeight", errors, {
                    maxVal: StringUtilities.numberCommaDecimal(MAX_HEIGHT),
                  })}
                  fullWidth
                  hasError={errors.ShipmentHeight != null}
                  id="shipment-height"
                  label={`${t("LABEL_UNIT_HEIGHT")} (CM)`}
                  placeholder={t("PLACEHOLDER_HEIGHT")}
                  required
                  {...register("ShipmentHeight")}
                />
              </InputGroup>
              <PositiveNumberInput
                errorMessage={getErrorMessage("ShipmentWeight", errors, {
                  weight: StringUtilities.numberCommaDecimal(MAX_WEIGHT),
                })}
                fullWidth
                hasError={errors.ShipmentWeight != null}
                id="shipment-weight"
                label={`${t("LABEL_UNIT_WEIGHT")} (KG)`}
                placeholder={t("PLACEHOLDER_WEIGHT")}
                required
                {...register("ShipmentWeight")}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
              <div className="flex flex-col gap-4 md:col-start-5 uppercase">
                <Typography textCase="upper">
                  {`${t("LABEL_TOTAL_VOLUME")} (M3)`}
                </Typography>
                {formatNumber(
                  getTotalVolume(
                    parseInt(watch("ShipmentQuantity")),
                    parseInt(watch("ShipmentLength")),
                    parseInt(watch("ShipmentWidth")),
                    parseInt(watch("ShipmentHeight"))
                  )
                )}
              </div>
              <div className="flex flex-col gap-4 uppercase">
                <Typography textCase="upper">
                  {`${t("LABEL_TOTAL_WEIGHT")} (KG) `}
                </Typography>
                {formatNumber(
                  getTotalWeight(
                    parseInt(watch("ShipmentQuantity")),
                    parseInt(watch("ShipmentWeight"))
                  )
                )}
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Page size="medium" title={t("MENU_REQUESTQUOTE")}>
      {location?.state?.quote?.QuoteReference && (
        <InlineAlert className="mb-12" variant="info">
          {t("TEXT_REQUEST_QUOTE_UPDATE_INFO", {
            quoteReference: location.state.quote.QuoteReference,
          })}
        </InlineAlert>
      )}

      <Form id="quote_booking_form" onSubmit={handleSubmit(onSubmit)}>
        <Section>
          <Group>
            <CheckForm
              id="party_consignee"
              key="party_consignee"
              label={t("LABEL_TOGGLE_CONSIGNEE")}
              type="radio"
              value={AddressType.CEE}
              {...register("AddressType")}
            />
            <CheckForm
              id="party_shipper"
              key="party_shipper"
              label={t("LABEL_TOGGLE_SHIPPER")}
              type="radio"
              value={AddressType.SHP}
              {...register("AddressType")}
            />
          </Group>
          {userProfile?.SelectedCompany && (
            <div className="flex flex-col">
              {userProfile.SelectedCompany.FullName && (
                <span className="font-bold">
                  {userProfile.SelectedCompany.FullName}
                </span>
              )}
              {userProfile.SelectedCompany.Address1 && (
                <span>{userProfile.SelectedCompany.Address1}</span>
              )}
              {userProfile.SelectedCompany.Address2 && (
                <span>{userProfile.SelectedCompany.Address2}</span>
              )}
              {userProfile.SelectedCompany.Country && (
                <span>{userProfile.SelectedCompany.Country}</span>
              )}
              {userProfile.SelectedCompany.Currency && (
                <span className="font-bold">
                  {userProfile.SelectedCompany.Currency}
                </span>
              )}
            </div>
          )}
        </Section>
        <Divider />
        <Grid gap={8} mdCol={6} smCols={1}>
          <Grid.Item mdSpan={2}>
            <Section title={t("LABEL_FROM")}>
              <Group onChange={resetOnPickupPlaceChange}>
                <CheckForm
                  id="pickup_from"
                  label={t("LABEL_PLACE_OF_RECEIPT")}
                  type="radio"
                  value={TransferPlace.ADDRESS}
                  {...register("PickupPlace")}
                />
                <CheckForm
                  id="port_from"
                  label={t("LABEL_SELF_DELIVER_PORT")}
                  type="radio"
                  value={TransferPlace.PORT}
                  {...register("PickupPlace")}
                />
              </Group>
              {getValues("PickupPlace") === TransferPlace.ADDRESS ? (
                <>
                  <WithFormController control={control} name="PLR">
                    <AsyncSelect
                      defaultOptions={defaultPlaceCities}
                      errorMessage={getErrorMessage("PLR", errors)}
                      fullWidth
                      getOptionLabel={portLabelMapper}
                      getOptionValue={portValueMapper}
                      hasError={errors.PLR != null}
                      id="place-of-receipt"
                      label={t("LABEL_CITY_COUNTRY")}
                      loadOptions={searchPort}
                      onChange={async (port: PortTypes.Port) =>
                        await onPointCodeSelect(port, "PLR")
                      }
                      placeholder={t("PLACEHOLDER_CITY")}
                      required
                    />
                  </WithFormController>
                  <Input
                    {...register("PickupPostalCode")}
                    errorMessage={getErrorMessage("PickupPostalCode", errors)}
                    fullWidth
                    hasError={errors.PickupPostalCode != null}
                    id="pick-up-postal-code"
                    label={t("LABEL_POSTALCODE")}
                    placeholder={t("PLACEHOLDER_POSTAL_CODE")}
                  />
                </>
              ) : (
                <WithFormController control={control} name="POL">
                  <Select
                    errorMessage={getErrorMessage("POL", errors)}
                    fullWidth
                    getOptionLabel={portLabelMapper}
                    getOptionValue={portValueMapper}
                    hasError={errors.POL != null}
                    label={t("LABEL_PORT_OF_LOAD")}
                    onChange={updateIncotermPlaces}
                    options={ports}
                    placeholder={t("PLACEHOLDER_SELECT_PORT")}
                    required
                  />
                </WithFormController>
              )}
            </Section>
          </Grid.Item>
          <Grid.Item className="!col-start-4" mdSpan={2}>
            <Section title={t("LABEL_TO")}>
              <Group onChange={resetOnDropOffPlaceChange}>
                <CheckForm
                  {...register("DropOffPlace")}
                  id="dropOff_to"
                  label={t("LABEL_PLACE_OF_DELIVERY")}
                  type="radio"
                  value={TransferPlace.ADDRESS}
                />
                <CheckForm
                  id="port_to"
                  label={t("LABEL_SELF_PICKUP_PORT")}
                  type="radio"
                  value={TransferPlace.PORT}
                  {...register("DropOffPlace")}
                />
              </Group>

              {getValues("DropOffPlace") === TransferPlace.ADDRESS ? (
                <>
                  <WithFormController control={control} name="PFD">
                    <AsyncSelect
                      defaultOptions={defaultPlaceCities}
                      errorMessage={getErrorMessage("PFD", errors)}
                      fullWidth
                      getOptionLabel={portLabelMapper}
                      getOptionValue={portValueMapper}
                      hasError={errors.PFD != null}
                      id="place-of-delivery"
                      label={t("LABEL_CITY_COUNTRY")}
                      loadOptions={searchPort}
                      onChange={async (port: PortTypes.Port) =>
                        await onPointCodeSelect(port, "PFD")
                      }
                      placeholder={t("PLACEHOLDER_CITY")}
                      required
                    />
                  </WithFormController>
                  <Input
                    {...register("DropOffPostalCode")}
                    errorMessage={getErrorMessage("DropOffPostalCode", errors)}
                    fullWidth
                    hasError={errors.DropOffPostalCode != null}
                    id="drop-off-postal-code"
                    label={t("LABEL_POSTALCODE")}
                    placeholder={t("PLACEHOLDER_POSTAL_CODE")}
                  />
                </>
              ) : (
                <WithFormController control={control} name="POD">
                  <Select
                    errorMessage={getErrorMessage("POD", errors)}
                    fullWidth
                    getOptionLabel={portLabelMapper}
                    getOptionValue={portValueMapper}
                    hasError={errors.POD != null}
                    label={t("LABEL_PORT_OF_DISCHARGE")}
                    onChange={updateIncotermPlaces}
                    options={ports}
                    placeholder={t("PLACEHOLDER_SELECT_PORT")}
                    required
                  />
                </WithFormController>
              )}
            </Section>
          </Grid.Item>
        </Grid>
        <Divider />
        <Section title={t("LABEL_SHIPMENT")}>
          <Group
            onChange={onShipmentTypeChange}
            title={t("LABEL_SHIPMENTTYPE")}
          >
            <CheckForm
              id="FCL_shipment"
              key="FCL_shipment"
              label={t("LABEL_FCLDEFINITION")}
              type="radio"
              value={ShipmentType.FCL}
              {...register("ShipmentType")}
            />
            <CheckForm
              id="LCL_shipment"
              key="LCL_shipment"
              label={t("LABEL_LCLDEFINITION")}
              type="radio"
              value={ShipmentType.LCL}
              {...register("ShipmentType")}
            />
            <CheckForm
              id="CAR_shipment"
              key="CAR_shipment"
              label={t("LABEL_CAR")}
              type="radio"
              value={ShipmentType.CAR}
              {...register("ShipmentType")}
            />
          </Group>
          {getShipmentInput(getValues("ShipmentType"))}
        </Section>
        <Divider />
        <Section required title={t("LABEL_COMMODITY")}>
          <Grid mdCol={3}>
            <WithFormController control={control} name="Commodity">
              <Select
                errorMessage={getErrorMessage("Commodity", errors)}
                fullWidth
                getOptionLabel={commodityLabelMapper}
                getOptionValue={commodityValueMapper}
                hasError={errors.Commodity != null}
                onChange={setShipmentTemperatures}
                options={commodityOptions}
                placeholder={t("PLACEHOLDER_SELECT_COMMODITY")}
                required
              />
            </WithFormController>
          </Grid>
          <Group title={t("LABEL_SPECIAL_HANDLING")}>
            {getValues("ShipmentType") !== ShipmentType.CAR &&
              getValues("ShipmentType") !== ShipmentType.FCL && (
                <CheckForm
                  disabled={getValues("ShipmentType") === ShipmentType.CAR}
                  id="IsStackable"
                  label={t("LABEL_STACKABLE")}
                  {...register("IsStackable")}
                />
              )}
            <CheckForm
              disabled={getValues("ShipmentType") === ShipmentType.CAR}
              id="DangerousGoods"
              label={t("LABEL_HAZARDOUS_MATERIALS")}
              {...register("DangerousGoods")}
            />
            {getValues("ShipmentType") !== ShipmentType.CAR && (
              <Tooltip
                color="primary"
                content={t("LABEL_CHOOSE_REFRIGERATED_CONTAINER")}
                hidden={
                  watch("ShipmentType") !== ShipmentType.FCL ||
                  isPackageRefrigerated(watch("ShipmentPackaging"))
                }
              >
                <CheckForm
                  disabled={watch("ShipmentType") === ShipmentType.FCL}
                  id="IsTemperatureControlled"
                  label={t("INFO_TEMPERATURE")}
                  {...register("IsTemperatureControlled")}
                />
              </Tooltip>
            )}
          </Group>
          {watch("IsTemperatureControlled") && (
            <WithFormController control={control} name="ShipmentTemperature">
              <Select
                errorMessage={getErrorMessage("ShipmentTemperature", errors)}
                hasError={errors.ShipmentTemperature != null}
                label={t("LABEL_TEMPERATURE")}
                options={temperatureOptions}
                placeholder={t("PLACEHOLDER_SELECT_TEMPERATURE")}
                required
              />
            </WithFormController>
          )}
        </Section>
        <Divider />
        <Section required title={t("LABEL_INCOTERMS")}>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <Tooltip
              color="primary"
              content={t("ERROR_SELECTPOLPODFIRST")}
              hidden={!!(watch("POL") && watch("POD"))}
            >
              <WithFormController control={control} name="Incoterms">
                <Select
                  disabled={!watch("POL") || !watch("POD")}
                  errorMessage={getErrorMessage("Incoterms", errors)}
                  fullWidth
                  getOptionLabel={incotermLabelMapper}
                  getOptionValue={incotermValueMapper}
                  hasError={errors.Incoterms != null}
                  label={t("LABEL_TERMS")}
                  onChange={updateIncotermPlaces}
                  options={filteredIncoterms}
                  placeholder={t("PLACEHOLDER_SELECT_INCOTERMS")}
                  required
                />
              </WithFormController>
            </Tooltip>

            {watch("Incoterms") && incotermPlaceOptions.length > 1 && (
              <WithFormController control={control} name="IncotermPoint">
                <Select
                  disabled={
                    !watch("Incoterms") || incotermPlaceOptions.length <= 1
                  }
                  fullWidth
                  getOptionLabel={portLabelMapper}
                  getOptionValue={portValueMapper}
                  label={t("LABEL_PLACE")}
                  options={incotermPlaceOptions}
                  placeholder={t("LABEL_PLACEHOLDER_SELECT")}
                />
              </WithFormController>
            )}
          </div>
        </Section>
        <Divider />
        <Section required title={t("LABEL_CONTACT")}>
          <div className="grid grid-cols-1 gap-8 justify-items-start md:grid-cols-3">
            <Input
              {...register("ContactName")}
              errorMessage={getErrorMessage("ContactName", errors)}
              fullWidth
              hasError={errors.ContactName != null}
              id="contact-name"
              label={t("LABEL_NAME")}
              required
            />
            <Input
              {...register("ContactEmail")}
              errorMessage={getErrorMessage("ContactEmail", errors)}
              fullWidth
              hasError={errors.ContactEmail != null}
              id="contact-email"
              label={t("LABEL_EMAIL")}
              required
            />
          </div>
        </Section>
        <Divider />
        <Section title={t("LABEL_OTHER_INFORMATION")}>
          <TextArea
            id="comment"
            {...register("Comment")}
            fullWidth
            placeholder={t("TEXT_ENTER_DETAILS")}
          />
        </Section>
        <div className="flex flex-row justify-end">
          <Button
            form="quote_booking_form"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            {t("LABEL_SEND_REQUEST")}
          </Button>
        </div>
      </Form>
      <LoadingModal isOpen={isSubmitting}>
        {t("TEXT_LOADING_QUOTE_REQUEST")}
      </LoadingModal>
    </Page>
  );
};

export default QuoteCreate;
